import React from 'react';
import moment from 'moment';
import { Text, View, Page } from '@react-pdf/renderer';
import { formatDOBFromPim, formatLocDateMMDDYYYY, formatPhoneNumber } from '@mummssoftware/utils/formatters';
import { ATTENDING_PHYSICIAN } from '@mummssoftware/utils/constants';
import { compareCertsByStartDateDescOrder } from '@mummssoftware/utils/comparators';
import { styles } from '../../utils/styles';
import { PrintHeaderPDFComponent } from '../Header';
import { PrintFooterPDFComponent } from '../Footer';
import { ios, pageOrientation, pageSize, safari } from '../../utils';
import defaults from '../../translations/en.json';
import { PrintSelections } from './PrintSelections';
const listStyle = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '3cm',
  flexWrap: 'wrap'
};

var _ref = /*#__PURE__*/React.createElement(Text, null);

const CertHistory = ({
  patientCertHistory
}) => {
  let sortedCerts;

  if (patientCertHistory) {
    const patientWithCerts = patientCertHistory[0];
    const {
      certHistory
    } = patientWithCerts;
    const certList = Object.values(certHistory).flat(1);
    sortedCerts = certList.sort(compareCertsByStartDateDescOrder);
  }

  return /*#__PURE__*/React.createElement(View, {
    style: [styles.marginYPointTwoCm]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.row, styles.fontSizeEightPt, styles.fontWeightBold, styles.pYPointOneCm]
  }, defaults.certifications, ":"), /*#__PURE__*/React.createElement(View, {
    style: [styles.width100, listStyle]
  }, sortedCerts ? sortedCerts.map(cert => {
    const formattedStart = formatLocDateMMDDYYYY(cert.startDate);
    const formattedEnd = formatLocDateMMDDYYYY(cert.actualEndDate || cert.expectedEndDate);
    return /*#__PURE__*/React.createElement(Text, {
      style: [styles.width33, styles.fontSizeEightPt]
    }, /*#__PURE__*/React.createElement(Text, {
      style: [styles.fontWeightBold]
    }, "#", cert.electionNumber, ":"), /*#__PURE__*/React.createElement(Text, null, formattedStart, " - ", formattedEnd));
  }) : _ref));
};

var _ref2 = /*#__PURE__*/React.createElement(View, null);

var _ref3 = /*#__PURE__*/React.createElement(View, null);

var _ref4 = /*#__PURE__*/React.createElement(PrintFooterPDFComponent, null);

export const CoversheetPDFComponent = ({
  patient,
  patientCertHistory,
  browser,
  siteTimezone,
  dashboardPrintConfig,
  sourceString
}) => {
  var _patientPhysicianDTOL;

  const {
    diagnoses,
    primaryPhone,
    primaryAddress,
    dischargeReason,
    admittedDate,
    teamPersonList,
    facility,
    allergies,
    dnr,
    referredDate,
    dischargeDate,
    patientPhysicianDTOList,
    insuranceCoverageDTOList,
    dateOfBirth,
    episodes
  } = patient;
  const patientAddress = `${primaryAddress.street1 || ''}, ${primaryAddress.city || ''}, ${primaryAddress.state || ''} ${primaryAddress.zip || ''}`;
  const attendingPhysician = patientPhysicianDTOList === null || patientPhysicianDTOList === void 0 ? void 0 : (_patientPhysicianDTOL = patientPhysicianDTOList.find(currentPhysician => currentPhysician && currentPhysician.physicianType === ATTENDING_PHYSICIAN)) === null || _patientPhysicianDTOL === void 0 ? void 0 : _patientPhysicianDTOL.physicianName;
  const sortedEpisodes = episodes && episodes.sort((epA, epB) => {
    if (!epA.admitDate) return 1;
    if (!epB.admitDate) return -1;
    return new Date(epB.admitDate).getTime() - new Date(epA.admitDate).getTime();
  }); // OP-32069: not using formatter methods as they will interfere with time zone
  // (they would reapply moment.utc or use the Date constructor)

  const shiftedDischargeDate = dischargeDate ? moment.utc(dischargeDate).tz(siteTimezone).format('MM/DD/YYYY HH:mm') : '';
  return /*#__PURE__*/React.createElement(Page, {
    wrap: false,
    size: pageSize,
    orientation: pageOrientation,
    style: browser && browser.name === safari || browser && browser.name === ios ? styles.pageSafari : styles.page
  }, /*#__PURE__*/React.createElement(PrintHeaderPDFComponent, {
    browser: browser,
    patient: patient
  }), dashboardPrintConfig ? /*#__PURE__*/React.createElement(PrintSelections, {
    printConfig: dashboardPrintConfig,
    sourceString: sourceString
  }) : _ref2, /*#__PURE__*/React.createElement(View, {
    style: styles.coverSheetSquare
  }, /*#__PURE__*/React.createElement(View, {
    style: [styles.row, styles.width100]
  }), /*#__PURE__*/React.createElement(View, {
    style: [styles.width100, styles.paddingPointTwoCm]
  }, /*#__PURE__*/React.createElement(View, {
    style: [styles.row, styles.width100, styles.marginYPointTwoCm]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.width20, styles.fontSizeEightPt]
  }, `${patient.firstName || ''} ${patient.lastName || ''}`), /*#__PURE__*/React.createElement(Text, {
    style: [styles.width50, styles.fontSizeEightPt]
  }, /*#__PURE__*/React.createElement(Text, null, patientAddress || ''))), /*#__PURE__*/React.createElement(View, {
    style: [styles.row, styles.width100, styles.marginYPointTwoCm]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.width33, styles.fontSizeEightPt]
  }, /*#__PURE__*/React.createElement(Text, {
    style: styles.fontWeightBold
  }, `${defaults.dob}: `), /*#__PURE__*/React.createElement(Text, null, `${dateOfBirth ? formatDOBFromPim(dateOfBirth) : ''}`)), /*#__PURE__*/React.createElement(Text, {
    style: [styles.width33, styles.fontSizeEightPt]
  }, /*#__PURE__*/React.createElement(Text, {
    style: styles.fontWeightBold
  }, `${defaults.phone}: `), /*#__PURE__*/React.createElement(Text, null, primaryPhone ? formatPhoneNumber(primaryPhone) : '')), /*#__PURE__*/React.createElement(Text, {
    style: [styles.width33, styles.fontSizeEightPt]
  }, /*#__PURE__*/React.createElement(Text, {
    style: styles.fontWeightBold
  }, `${defaults.currentLocation}: `), /*#__PURE__*/React.createElement(Text, null, facility || ''))), /*#__PURE__*/React.createElement(View, {
    style: [styles.row, styles.width100, styles.marginYPointTwoCm]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.width33, styles.fontSizeEightPt]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontWeightBold]
  }, `${defaults.physician}: `), /*#__PURE__*/React.createElement(Text, null, typeof attendingPhysician === 'string' ? attendingPhysician : '')), /*#__PURE__*/React.createElement(Text, {
    style: [styles.width33, styles.fontSizeEightPt]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontWeightBold]
  }, `${defaults.idgTeam}: `), /*#__PURE__*/React.createElement(Text, null, teamPersonList || '')), /*#__PURE__*/React.createElement(Text, {
    style: [styles.width33, styles.fontSizeEightPt]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontWeightBold]
  }, `${defaults.dnr}: `), /*#__PURE__*/React.createElement(Text, null, dnr || ''))), /*#__PURE__*/React.createElement(View, {
    style: [styles.width100, styles.marginYPointTwoCm]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontWeightBold, styles.fontSizeEightPt]
  }, `${defaults.diagnosis}: `), /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontSizeEightPt, styles.pYPointOneCm]
  }, diagnoses ? diagnoses.toString() : '')), /*#__PURE__*/React.createElement(View, {
    style: [styles.width100, styles.marginYPointTwoCm]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontWeightBold, styles.fontSizeEightPt]
  }, `${defaults.allergies}: `), /*#__PURE__*/React.createElement(Text, {
    style: styles.fontSizeEightPt
  }, allergies ? allergies.toString() : '')), /*#__PURE__*/React.createElement(View, {
    style: [styles.row, styles.width100, styles.marginYPointTwoCm]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.width20, styles.fontSizeEightPt]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontWeightBold]
  }, defaults.admissionHistory, ":"))), /*#__PURE__*/React.createElement(View, {
    style: [styles.row, styles.width100, styles.marginYPointTwoCm]
  }, /*#__PURE__*/React.createElement(View, {
    style: styles.width33
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontWeightBold, styles.fontSizeEightPt]
  }, `${defaults.referralDate}:`), /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontSizeEightPt, styles.pYPointOneCm]
  }, referredDate ? formatLocDateMMDDYYYY(referredDate) : '')), /*#__PURE__*/React.createElement(View, {
    style: styles.width33
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontWeightBold, styles.fontSizeEightPt]
  }, `${defaults.admitDate}:`), /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontSizeEightPt, styles.pYPointOneCm]
  }, admittedDate ? formatLocDateMMDDYYYY(admittedDate) : '')), /*#__PURE__*/React.createElement(View, {
    style: styles.width33
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontWeightBold, styles.fontSizeEightPt]
  }, `${defaults.dischargeDate}:`), /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontSizeEightPt, styles.pYPointOneCm]
  }, shiftedDischargeDate || '')), /*#__PURE__*/React.createElement(View, {
    style: styles.width33
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontWeightBold, styles.fontSizeEightPt]
  }, `${defaults.dischargeType}:`), /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontSizeEightPt, styles.pYPointOneCm]
  }, dischargeReason || ''))), /*#__PURE__*/React.createElement(View, {
    style: [styles.marginYPointTwoCm]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.row, styles.fontSizeEightPt, styles.fontWeightBold, styles.pYPointOneCm]
  }, defaults.episodes, ":"), /*#__PURE__*/React.createElement(View, {
    style: [styles.width100, listStyle]
  }, sortedEpisodes.map(ep => /*#__PURE__*/React.createElement(Text, {
    style: [styles.width33, styles.fontSizeEightPt]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontWeightBold]
  }, ep.program, ": "), /*#__PURE__*/React.createElement(Text, null, ep.admitDate, " - ", ep.dischargeDate))))), /*#__PURE__*/React.createElement(CertHistory, {
    patientCertHistory: patientCertHistory
  }), /*#__PURE__*/React.createElement(View, {
    style: [styles.marginYPointTwoCm]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.row, styles.fontSizeEightPt, styles.fontWeightBold, styles.pYPointOneCm]
  }, defaults.paySources, ":"), /*#__PURE__*/React.createElement(View, {
    style: [styles.width100, listStyle]
  }, insuranceCoverageDTOList ? insuranceCoverageDTOList.map(source => {
    const formattedEffective = source.effectiveDate && formatLocDateMMDDYYYY(source.effectiveDate);
    return /*#__PURE__*/React.createElement(Text, {
      style: [styles.width50, styles.fontSizeEightPt]
    }, /*#__PURE__*/React.createElement(Text, {
      style: [styles.fontWeightBold]
    }, source.carrierName), /*#__PURE__*/React.createElement(Text, null, formattedEffective || '', " ", source.policyNumber));
  }) : _ref3)))), _ref4);
};